/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  onBeforeUnmount,
  onMounted,
  ref,
  Transition,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";
import { claimCoins } from "@app/server/client";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useCustomer } from "../../providers/auth-context";
import {
  CollectedBottomNav,
  COLLECTED_BOTTOM_NAV_DESKTOP_CLEARANCE,
  COLLECTED_BOTTOM_NAV_MOBILE_CLEARANCE,
} from "./CollectedBottomNav";
import { CollectedWalletStage } from "./CollectedWalletStage";
import { PlaceFan } from "./PlaceFan";
import { SignedOutHero } from "./SignedOutHero";
import { SignedOutSkeleton } from "./SignedOutSkeleton";
import { createFanTimeline } from "./fanTimeline";
import {
  COIN_PREFIX,
  MEM_PREFIX,
  type Place,
} from "./placeTypes";
import { useCollectedWallet } from "./useCollectedWallet";

const MOBILE_MAX_WIDTH = 600;

const CONFETTI_COLORS = [
  "bg-primary",
  "bg-secondary",
  "bg-accent",
  "bg-success",
  "bg-warning",
];

/** A non-blocking burst over the wallet; the claimed card remains usable. */
const ClaimConfetti = defineComponent({
  name: "CollectedClaimConfetti",
  setup() {
    return () => (
      <div
        class="pointer-events-none fixed inset-0 z-[60] overflow-hidden"
        aria-hidden="true"
      >
        {Array.from({ length: 36 }).map((_, index) => (
          <i
            key={index}
            class={[
              "claim-confetti absolute left-1/2 top-[52%] block h-3 w-1.5 rounded-full",
              CONFETTI_COLORS[index % CONFETTI_COLORS.length],
            ]}
            style={{
              "--confetti-x": `${((index * 47) % 190) - 95}vw`,
              "--confetti-y": `${-35 - ((index * 29) % 45)}vh`,
              "--confetti-r": `${180 + ((index * 83) % 540)}deg`,
              "--confetti-delay": `${80 + ((index * 31) % 280)}ms`,
            }}
          />
        ))}
      </div>
    );
  },
});

const shimmer = (classes: string) => (
  <div class={`rounded skeleton-shimmer ${classes}`} />
);

const CollectedSkeleton = defineComponent<{ fill?: boolean; grid?: boolean }>({
  name: "CollectedSkeleton",
  props: ["fill", "grid"],
  setup(props) {
    const summary = () => (
      <div class="grid shrink-0 grid-cols-3 divide-x divide-base-300 overflow-hidden rounded-2xl border border-base-300 bg-base-100 my-2 mx-2">
        {[0, 1, 2].map((index) => (
          <div key={index} class="flex justify-center px-2 py-2">
            {shimmer("h-4 w-20")}
          </div>
        ))}
      </div>
    );

    return () => (
      <div
        class={[
          "flex min-h-0 flex-col gap-3 sm:gap-5",
          props.fill ? "flex-1" : "",
        ]}
        aria-busy="true"
      >
        {summary()}
        {props.grid ? (
          <div
            class="grid auto-rows-[minmax(30rem,1fr)] grid-cols-2 gap-x-8 gap-y-10 px-5 pt-6 lg:grid-cols-3"
            style={{
              paddingBottom: `${COLLECTED_BOTTOM_NAV_DESKTOP_CLEARANCE}px`,
            }}
          >
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <div
                key={index}
                class="relative isolate h-full w-full rounded-3xl"
              >
                {/* The final slot represents the unstacked How-to card. */}
                {index < 5 && (
                  <>
                    <div
                      aria-hidden="true"
                      class="absolute inset-0 overflow-hidden rounded-3xl bg-base-300 shadow-lg skeleton-shimmer"
                      style={{
                        opacity: "0.5",
                        transform:
                          "translate(-13px, -16px) rotate(-5.5deg) scale(0.96)",
                      }}
                    />
                    <div
                      aria-hidden="true"
                      class="absolute inset-0 overflow-hidden rounded-3xl bg-base-300 shadow-lg skeleton-shimmer"
                      style={{
                        opacity: "0.38",
                        transform:
                          "translate(12px, -8px) rotate(4.5deg) scale(0.96)",
                      }}
                    />
                  </>
                )}
                <div class="relative z-10 h-full w-full rounded-3xl skeleton-shimmer shadow-xl" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div
              class={[
                "w-full rounded-3xl skeleton-shimmer shadow-xl",
                props.fill
                  ? "min-h-0 flex-1"
                  : "aspect-[5/7] sm:mx-auto sm:max-w-[21rem]",
              ]}
            />
            <div class="flex shrink-0 items-center justify-center gap-2 pt-1">
              {shimmer("h-7 w-24")}
              {shimmer("h-7 w-24")}
            </div>
          </>
        )}
      </div>
    );
  },
});

const EmptyState = defineComponent<{
  icon: string;
  title: string;
  copy: string;
  action?: () => any;
}>({
  name: "CollectedEmptyState",
  props: ["icon", "title", "copy", "action"],
  setup(props) {
    return () => (
      <div class="rounded-3xl border border-base-300 bg-base-100 px-6 py-14 text-center shadow-sm">
        <span class="mx-auto grid size-14 place-items-center rounded-2xl bg-base-200 text-base-content/30">
          <span class="material-symbols-outlined text-3xl" aria-hidden="true">
            {props.icon}
          </span>
        </span>
        <h2 class="mt-4 text-lg font-bold text-base-content">{props.title}</h2>
        <p class="mx-auto mt-1 max-w-sm text-sm text-base-content/60">
          {props.copy}
        </p>
        {props.action?.()}
      </div>
    );
  },
});

export const CollectedPage = defineComponent({
  name: "CollectedPage",
  setup() {
    const { t } = useLocalization();
    const router = useRouter();
    const route = useRoute();
    const wallet = useCollectedWallet();
    const trpc = useTrpc();
    const auth = useCustomer();
    const hasSignedOutSession = computed(
      () => !auth.customer.value || auth.customer.value.anon,
    );

    const paymentGuid = computed(() => {
      const value = route.query.payment;
      return Array.isArray(value) ? (value[0] ?? "") : (value ?? "");
    });
    const membershipProgramId = computed(() => {
      const value = route.query.membership_program;
      return Array.isArray(value) ? (value[0] ?? "") : (value ?? "");
    });
    const claimError = ref<string | null>(null);
    const claimingReceipt = ref(false);
    const claimFocusKey = ref<string | null>(null);
    const confettiKey = ref(0);
    let activeReceiptAction: string | null = null;
    let confettiTimer: ReturnType<typeof setTimeout> | undefined;

    const celebrateClaim = () => {
      confettiKey.value += 1;
      clearTimeout(confettiTimer);
      confettiTimer = setTimeout(() => (confettiKey.value = 0), 2400);
    };

    // The advertised membership, fetched via a public endpoint so the signed-out
    // hero can name the actual program and its perks before an account exists.
    const programOffer = ref<{
      name: string;
      description: string | null;
      place_name: string;
    } | null>(null);
    watch(
      [membershipProgramId, paymentGuid],
      async ([membershipId, guid]) => {
        if (!membershipId || !guid) {
          programOffer.value = null;
          return;
        }
        try {
          programOffer.value = await trpc.membershipPrograms.receiptOffer.query(
            {
              paymentGuid: guid,
              programId: membershipId,
            },
          );
        } catch {
          programOffer.value = null;
        }
      },
      { immediate: true },
    );

    const isMobile = ref(false);
    const selectedView = ref<"stack" | "grid">("grid");
    const view = computed<"stack" | "grid">(() =>
      isMobile.value ? "stack" : selectedView.value,
    );
    const syncViewport = () => {
      isMobile.value = window.innerWidth < MOBILE_MAX_WIDTH;
    };
    onMounted(() => {
      syncViewport();
      window.addEventListener("resize", syncViewport);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("resize", syncViewport);
      clearTimeout(confettiTimer);
    });

    const placeByKey = (key: string | null) =>
      key
        ? (wallet.places.value.find((place) => place.key === key) ?? null)
        : null;

    const focusItemKey = computed<string | null>(() => {
      if (claimFocusKey.value) return claimFocusKey.value;
      const value = route.query.membership_program;
      const id = Array.isArray(value) ? value[0] : value;
      return id ? MEM_PREFIX + id : null;
    });
    const focusPlace = computed<Place | null>(() =>
      focusItemKey.value
        ? (wallet.places.value.find((place) =>
            place.items.some((item) => item.key === focusItemKey.value),
          ) ?? null)
        : null,
    );
    const focusPlaceKey = computed(() => focusPlace.value?.key ?? null);

    const clearReceiptQuery = () => {
      const {
        payment: _payment,
        membership_program: _membershipProgram,
        ...query
      } = route.query;
      void router.replace({ query });
    };

    // The wallet's initial request may have completed while the visitor was
    // signed out. Refresh it when the auth dialog turns that same page session
    // into an authenticated one so the newly available cards are rendered.
    watch(
      () => auth.customer.value,
      (customer, previousCustomer) => {
        if (
          customer &&
          !customer.anon &&
          (!previousCustomer || previousCustomer.anon)
        ) {
          void wallet.load(true);
        }
      },
    );

    // Receipt visitors should be able to authenticate immediately. Keep the
    // welcome screen mounted behind the dialog, and only auto-open once for a
    // given receipt so closing it remains meaningful; its CTA can reopen it.
    let autoAuthPromptKey: string | null = null;
    watch(
      [paymentGuid, membershipProgramId, wallet.signedOut],
      ([guid, membershipId, signedOut]) => {
        if (!guid || !signedOut || !hasSignedOutSession.value) return;
        const promptKey = `${guid}:${membershipId || "coins"}`;
        if (autoAuthPromptKey === promptKey) return;
        autoAuthPromptKey = promptKey;
        auth.showAuthDialog();
      },
      { immediate: true },
    );

    watch(
      [paymentGuid, membershipProgramId, () => auth.customer.value],
      async ([guid, membershipId, customer]) => {
        if (!guid) return;
        // The welcome screen stays behind the automatically opened auth dialog.
        // Claiming resumes when this watch re-fires with a verified customer.
        if (!customer || customer.anon) return;
        const actionKey = `${guid}:${membershipId || "coins"}`;
        if (activeReceiptAction === actionKey) return;
        activeReceiptAction = actionKey;
        claimError.value = null;
        claimingReceipt.value = true;

        // Load the existing wallet before mutating it, then refresh after the
        // claim so the newly current card can be focused in the live deck.
        await wallet.load(true);

        try {
          if (membershipId) {
            const result = await trpc.membershipPrograms.joinReceipt.mutate({
              paymentGuid: guid,
              programId: membershipId,
            });
            await wallet.load(true);

            const key = MEM_PREFIX + result.program.program_id;
            claimFocusKey.value = key;
            selectedView.value = "stack";
            clearReceiptQuery();
            if (result.status === "enrolled") celebrateClaim();
            return;
          }

          const result = await claimCoins(guid);
          if (
            result.status !== "claimed" &&
            result.status !== "already_claimed"
          ) {
            claimError.value = t(`customer.coins.refused_${result.status}`);
            activeReceiptAction = null;
            return;
          }

          await wallet.load(true);
          clearReceiptQuery();

          // A previously collected receipt is idempotent and should quietly
          // open the wallet, not replay a celebration for coins added earlier.
          if (result.status === "already_claimed") return;

          const claimedProgram = result.programs[0];
          if (claimedProgram) {
            claimFocusKey.value = COIN_PREFIX + claimedProgram.coin_program_id;
            selectedView.value = "stack";
            celebrateClaim();
          }
        } catch {
          claimError.value = t(
            membershipId
              ? "customer.collected.membership_join_failed"
              : "customer.coins.claim_failed",
          );
          activeReceiptAction = null;
        } finally {
          claimingReceipt.value = false;
        }
      },
      { immediate: true },
    );

    const routeFanKey = computed<string | null>(() => {
      const value = route.query.place;
      return typeof value === "string" ? value : null;
    });
    const fanSource = ref<DOMRect | null>(null);
    const fanPlace = ref<Place | null>(null);
    const fanVisible = ref(false);
    const ownsFanHistory = ref(false);

    watch(
      [routeFanKey, wallet.places],
      ([key]) => {
        const place = placeByKey(key);
        if (place) {
          fanPlace.value = place;
          fanVisible.value = true;
        } else {
          fanVisible.value = false;
        }
      },
      { immediate: true },
    );

    const openFan = (key: string, source: HTMLElement | null) => {
      fanSource.value = source?.getBoundingClientRect() ?? null;
      ownsFanHistory.value = true;
      void router.push({ query: { ...route.query, place: key } });
    };

    const queryWithoutPlace = () => {
      const { place: _place, ...query } = route.query;
      return query;
    };
    const closeFan = () => {
      if (ownsFanHistory.value) void router.back();
      else void router.replace({ query: queryWithoutPlace() });
    };

    let deepLinkHandled = false;
    watch(
      focusPlace,
      (place) => {
        if (deepLinkHandled || !place) return;
        deepLinkHandled = true;
        if (place.items.length > 1 && !routeFanKey.value) {
          ownsFanHistory.value = true;
          void router.push({ query: { ...route.query, place: place.key } });
        }
      },
      { immediate: true },
    );

    watch(routeFanKey, (key, previousKey) => {
      if (!key && previousKey) ownsFanHistory.value = false;
    });

    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const fanTimeline = createFanTimeline(() => fanSource.value, reducedMotion);

    const renderAction =
      (label: string, action: string | (() => void)) => () => (
        <button
          type="button"
          class="btn btn-primary btn-sm mt-5"
          onClick={() =>
            typeof action === "string" ? router.push(action) : action()
          }
        >
          {label}
        </button>
      );

    const renderBody = () => {
      if (wallet.loading.value || claimingReceipt.value) {
        if (hasSignedOutSession.value) {
          return <SignedOutSkeleton />;
        }
        return (
          <CollectedSkeleton
            fill={isMobile.value}
            grid={view.value === "grid"}
          />
        );
      }
      if (wallet.signedOut.value) {
        return (
          <SignedOutHero
            pendingReward={Boolean(paymentGuid.value)}
            program={programOffer.value}
            onSignIn={() => auth.showAuthDialog()}
          />
        );
      }
      if (wallet.failed.value) {
        return (
          <EmptyState
            icon="error_outline"
            title={t("customer.collected.failed_title")}
            copy={t("customer.collected.failed_copy")}
          />
        );
      }
      if (wallet.places.value.length === 0) {
        return (
          <EmptyState
            icon="qr_code_scanner"
            title={t("customer.collected.empty_title")}
            copy={t("customer.collected.empty_copy")}
            action={renderAction(t("customer.collected.empty_cta"), "/")}
          />
        );
      }
      return (
        <CollectedWalletStage
          places={wallet.places.value}
          totals={wallet.totals.value}
          images={wallet.passImages.value}
          view={view.value}
          isMobile={isMobile.value}
          focusPlaceKey={focusPlaceKey.value}
          onChangeView={(nextView) => (selectedView.value = nextView)}
          onOpenPlace={openFan}
        />
      );
    };

    return () => (
      <div
        class={[
          "bg-base-200/40 text-base-content",
          isMobile.value &&
          !wallet.signedOut.value &&
          !hasSignedOutSession.value
            ? "flex h-[100dvh] flex-col overflow-hidden"
            : "min-h-screen",
        ]}
      >
        {claimError.value && (
          <div class="fixed left-1/2 top-4 z-[65] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-start gap-2 rounded-2xl border border-error/20 bg-base-100 p-4 text-sm text-error shadow-xl">
            <span class="material-symbols-outlined text-lg" aria-hidden="true">
              error_outline
            </span>
            <span class="flex-1">{claimError.value}</span>
            <button
              type="button"
              class="btn btn-ghost btn-xs btn-circle"
              aria-label="Close"
              onClick={() => (claimError.value = null)}
            >
              <span class="material-symbols-outlined text-sm">close</span>
            </button>
          </div>
        )}
        <main
          class={[
            " mx-auto w-full overflow-x-hidden ",
            view.value === "grid" ? "max-w-6xl" : "max-w-2xl",
            isMobile.value &&
            !wallet.signedOut.value &&
            !hasSignedOutSession.value
              ? "flex min-h-0 flex-1 flex-col"
              : "",
          ]}
          style={
            isMobile.value &&
            !wallet.signedOut.value &&
            !hasSignedOutSession.value
              ? { paddingBottom: COLLECTED_BOTTOM_NAV_MOBILE_CLEARANCE }
              : undefined
          }
        >
          {renderBody()}
        </main>

        <CollectedBottomNav />

        {confettiKey.value > 0 && <ClaimConfetti key={confettiKey.value} />}

        <Transition
          css={false}
          onEnter={fanTimeline.enter}
          onLeave={fanTimeline.leave}
          onAfterLeave={() => {
            if (!fanVisible.value) fanPlace.value = null;
          }}
        >
          {fanVisible.value && fanPlace.value ? (
            <PlaceFan
              key={fanPlace.value.key}
              place={fanPlace.value}
              images={wallet.passImages.value}
              initialFocusKey={focusItemKey.value}
              onClose={closeFan}
            />
          ) : null}
        </Transition>
      </div>
    );
  },
});
